<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsPoints from "@apps/rewards/components/RewardsPoints.svelte";
    import RewardsSliderArrows from "@apps/rewards/components/RewardsSliderArrows.svelte";
    import RewardsVipTiersGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersGoal.svelte";
    import RewardsVipTiersPointsGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersPointsGoal.svelte";
    import RewardsVipTiersPurchasesGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersPurchasesGoal.svelte";
    import RewardsVipTiersReferralsGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersReferralsGoal.svelte";
    import RewardsVipTiersSpentAmountGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersSpentAmountGoal.svelte";
    import RewardsWidgetReward from "@apps/rewards/components/RewardsWidgetReward/RewardsWidgetReward.svelte";
    import { DATA_GW_SELECTORS } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
    import { PROGRAM_STATUS } from "@apps/rewards/modules/StorefrontValues/constants";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type RewardsVipTierProgressQuery from "@apps/rewards/modules/VipTiers/queries/RewardsVipTierProgressQuery";
    import type RewardsVipTiersQuery from "@apps/rewards/modules/VipTiers/queries/RewardsVipTiersQuery";
    import type RewardsVipTiersService from "@apps/rewards/modules/VipTiers/service/RewardsVipTiersService";
    import { REWARDS_VIP_TIERS_CONDITION_TYPES } from "@apps/rewards/modules/VipTiers/types";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import { selectRequestStatus } from "@apps/rewards/utils/statuses";
    import RewardsVipTiersPageWidgetTierStatus from "@apps/rewards/widgets/rewards_page_widgets/widgets/rewards_vip_tiers_page_widget/components/RewardsVipTiersPageWidgetTierStatus.svelte";
    import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import { getHumanReadableDate } from "@lib/utils";
    import type { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import type { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { debounceTime, fromEvent, map, startWith } from "rxjs";
    import { onMount } from "svelte";

    import Icon from "@components/Icon.svelte";
    import DotsIcon from "@components/icons/DotsIcon.svelte";
    import PreviewBadge from "@components/PreviewBadge.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import EmblaSliderDots from "@components/Slider/EmblaSliderDots.svelte";
    import type { EmblaSliderApi } from "@components/Slider/types";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";
    import type { LoggerInterface } from "@interfaces/LoggerInterface";

    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";
    import RewardsVipTiersPageWidgetEmptyItem from "./components/RewardsVipTiersPageWidgetEmptyItem.svelte";
    import RewardsVipTiersPageWidgetTheme from "./components/RewardsVipTiersPageWidgetTheme.svelte";
    import RewardsVipTiersPageWidgetTierCard from "./components/RewardsVipTiersPageWidgetTierCard.svelte";
    import RewardsVipTiersPageWidgetSkeleton from "./components/skeleton/RewardsVipTiersPageWidgetSkeleton.svelte";

    //Props
    export let vipTiersService: RewardsVipTiersService;
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let vipTiersQuery: RewardsVipTiersQuery;
    export let vipTierProgressQuery: RewardsVipTierProgressQuery;
    export let programAccountQuery: RewardsProgramAccountQuery;
    export let i18n: GwI18n;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let amountFormatter: RewardsAmountFormatter;

    export let urlsBuilder: URLsBuilder;
    export let sanitizer: HTMLSanitizerInterface;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let localizationInfoProvider: LocalizationInfoProviderInterface;
    export let dynamicImportManager: DynamicImportManager;
    export let logger: LoggerInterface;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;

    export let isDesignMode: boolean | null;
    export let afterWidgetInject: () => void;

    const branding$ = storefrontValuesService.selectRewardsBranding();
    const tierList$ = vipTiersService.selectAvailableTiers();
    const customerNextTier$ = vipTiersService.customerNextTier$;

    const firstTier$ = vipTiersQuery.firstTier$;
    const hasTiers$ = vipTiersQuery.hasTiers$;
    const tiersRequestStatus$ = vipTiersQuery.status$;

    const tierProgress$ = vipTierProgressQuery.tierProgress$;
    const tierProgressRequestStatus$ = vipTierProgressQuery.status$;
    const customerCurrentTier$ = programAccountQuery.customerCurrentTier$;

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    let emblaApi: EmblaSliderApi | null = null;
    let currentIndex = 0;

    function getCurrentSlidesPerPage(): number {
        const width = window.innerWidth;

        if (width >= 1200) return 3;
        if (width >= 768) return 2;
        return 1;
    }

    let slidesPerPage = getCurrentSlidesPerPage();

    const slidesPerPage$ = fromEvent(window, "resize").pipe(
        debounceTime(100),
        map(() => getCurrentSlidesPerPage()),
        startWith(getCurrentSlidesPerPage())
    );

    const isInitialLoading$ = selectRequestStatus(
        [tiersRequestStatus$, tierProgressRequestStatus$],
        STATUS.INITIAL_LOADING
    );

    function handleSelect() {
        if (emblaApi) {
            currentIndex = emblaApi.selectedScrollSnap();
        }
    }

    function handleReady() {
        if (emblaApi) {
            currentIndex = emblaApi.selectedScrollSnap();
        }
    }

    function handlePrev() {
        emblaApi?.scrollPrev();
    }

    function handleNext() {
        emblaApi?.scrollNext();
    }

    onMount(() => {
        const tiersDataSubscription = vipTiersService.getTiersData({
            programsStatus$: storefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.tiersProgramEnabled
            ),
            vipTiersService,
            tiersRequestStatus$,
            tierProgressRequestStatus$,
            isCustomer$,
            localizationInfoProvider,
        });

        afterWidgetInject();

        return () => tiersDataSubscription.unsubscribe();
    });

    $: tierSummary = $tierList$.reduce(
        (acc, tier) => {
            acc.hasPointsMultiplier ||= tier.pointsMultiplier !== 1;
            acc.hasReward ||= tier.rewarding !== null;
            acc.maxPerksLength = Math.max(
                acc.maxPerksLength,
                tier.perks.length
            );

            return acc;
        },
        { hasPointsMultiplier: false, hasReward: false, maxPerksLength: 0 }
    );
    $: slidesPerPage = $slidesPerPage$;
</script>

{#if $branding$ && $hasTiers$}
    <RewardsVipTiersPageWidgetTheme rewardsBranding={$branding$}>
        <div class="gw-rd-vip-tiers-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if isDesignMode}
                        <PreviewBadge />
                    {/if}

                    {#if $isInitialLoading$}
                        <RewardsVipTiersPageWidgetSkeleton />
                    {:else}
                        <RewardsPageWidgetTitle>
                            <T {namespace} translationKey="widget_title" />
                        </RewardsPageWidgetTitle>
                        <RewardsCustomerStatusBasedContent
                            isCustomerContent={$isCustomer$ &&
                                $firstTier$?.condition !==
                                    REWARDS_VIP_TIERS_CONDITION_TYPES.customerTag}
                        >
                            <RewardsVipTiersPageWidgetTierStatus
                                slot="customer-content"
                                customerCurrentTier={$customerCurrentTier$?.title}
                                customerNextTier={$customerNextTier$}
                                {i18n}
                                {currencyFormatter}
                                {amountFormatter}
                                {dynamicImportManager}
                                {logger}
                                {sanitizer}
                                {namespace}
                                {pointsNamespace}
                            />
                        </RewardsCustomerStatusBasedContent>
                        <div class="gw-rd-vip-tiers-page-widget__layout">
                            <div class="gw-rd-vip-tiers-page-widget__benefits">
                                <RewardsVipTiersPageWidgetTierCard
                                    perksLinesCount={tierSummary.maxPerksLength}
                                    hasPointsMultiplier={tierSummary.hasPointsMultiplier}
                                    hasReward={tierSummary.hasReward}
                                >
                                    <Text
                                        slot="goal"
                                        component="span"
                                        variant="base"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="vip_tiers_achievement_criteria"
                                        />
                                    </Text>
                                    <Text
                                        slot="points-multiplier"
                                        component="span"
                                        variant="base"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="vip_tiers_points_multiplier"
                                        />
                                    </Text>
                                    <Text
                                        slot="reward"
                                        component="span"
                                        variant="base"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="vip_tiers_reward"
                                        />
                                    </Text>
                                    <Text
                                        slot="perks"
                                        component="span"
                                        variant="base"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="vip_tiers_perks"
                                        />
                                    </Text>
                                </RewardsVipTiersPageWidgetTierCard>
                            </div>
                            <div class="gw-rd-vip-tiers-page-widget__slider">
                                <div
                                    class="gw-rd-vip-tiers-page-widget__slider-arrows-wrapper"
                                >
                                    <RewardsSliderArrows
                                        perPage={slidesPerPage}
                                        slidesCount={$tierList$.length}
                                        sliderApi={emblaApi || undefined}
                                        {currentIndex}
                                        onPrev={handlePrev}
                                        onNext={handleNext}
                                        prevButtonAriaLabel="Previous slide"
                                        nextButtonAriaLabel="Next slide"
                                    />
                                </div>
                                <EmblaSlider
                                    setApi={(api) => (emblaApi = api)}
                                    perPage={slidesPerPage}
                                    loop={false}
                                    duration={40}
                                    on:select={handleSelect}
                                    on:ready={handleReady}
                                >
                                    <svelte:fragment slot="slides">
                                        {#each $tierList$ as tier (tier.id)}
                                            {@const isCustomerTier =
                                                tier.id ===
                                                $customerCurrentTier$?.id}

                                            <RewardsVipTiersPageWidgetTierCard
                                                perksLinesCount={tierSummary.maxPerksLength}
                                                hasPointsMultiplier={tierSummary.hasPointsMultiplier}
                                                hasReward={tierSummary.hasReward}
                                                {isCustomerTier}
                                                isFirstTier={tier.id ===
                                                    $firstTier$.id}
                                                dataGwSelector={`${DATA_GW_SELECTORS.tierPage}-${tier.id}`}
                                            >
                                                <Text
                                                    slot="title"
                                                    component="h2"
                                                    variant="base"
                                                    size="md"
                                                >
                                                    {tier.title}
                                                </Text>
                                                <svelte:fragment
                                                    slot="description"
                                                >
                                                    {#if tier.description}
                                                        <Text variant="base">
                                                            {@html sanitizer.sanitize(
                                                                tier.description
                                                            )}
                                                        </Text>
                                                    {/if}
                                                </svelte:fragment>
                                                <Text
                                                    slot="goal"
                                                    component="span"
                                                    variant="base"
                                                >
                                                    {#if isCustomerTier && $customerCurrentTier$?.achievedAt}
                                                        <T
                                                            {namespace}
                                                            translationKey="vip_tiers_tier_achieved_on"
                                                            translationOptions={{
                                                                date: getHumanReadableDate(
                                                                    $customerCurrentTier$.achievedAt
                                                                ),
                                                            }}
                                                        />
                                                    {:else if tier.condition !== REWARDS_VIP_TIERS_CONDITION_TYPES.customerTag && !tier.isDefault}
                                                        <RewardsVipTiersGoal
                                                            tierCondition={tier.condition}
                                                        >
                                                            <RewardsPoints
                                                                slot="points-goal"
                                                                points={tier.conditionValue}
                                                                {amountFormatter}
                                                                {i18n}
                                                                {pointsNamespace}
                                                            >
                                                                <RewardsVipTiersPointsGoal
                                                                    let:value={{
                                                                        points,
                                                                        pointsName,
                                                                    }}
                                                                    slot="default"
                                                                    isCustomer={$isCustomer$}
                                                                    tierConditionValue={points}
                                                                    tierProgress$={amountFormatter.format(
                                                                        $tierProgress$
                                                                    )}
                                                                    {pointsName}
                                                                    {namespace}
                                                                />
                                                            </RewardsPoints>
                                                            <RewardsVipTiersPurchasesGoal
                                                                slot="purchases-goal"
                                                                isCustomer={$isCustomer$}
                                                                tierConditionValue={tier.conditionValue}
                                                                tierProgress={$tierProgress$}
                                                                {namespace}
                                                            />
                                                            <RewardsVipTiersSpentAmountGoal
                                                                slot="spent-amount-goal"
                                                                isCustomer={$isCustomer$}
                                                                tierConditionValue$={currencyFormatter.selectFormattedCurrency(
                                                                    tier.conditionValue
                                                                )}
                                                                tierProgress$={currencyFormatter.selectFormattedCurrency(
                                                                    $tierProgress$
                                                                )}
                                                                {namespace}
                                                            />
                                                            <RewardsVipTiersReferralsGoal
                                                                slot="referrals-goal"
                                                                isCustomer={$isCustomer$}
                                                                tierConditionValue={tier.conditionValue}
                                                                tierProgress={$tierProgress$}
                                                                {namespace}
                                                            />
                                                        </RewardsVipTiersGoal>
                                                    {:else}
                                                        <RewardsVipTiersPageWidgetEmptyItem
                                                        />
                                                    {/if}
                                                </Text>
                                                <svelte:fragment
                                                    slot="points-multiplier"
                                                >
                                                    {#if tier.pointsMultiplier && tier.pointsMultiplier !== 1}
                                                        <Text
                                                            component="span"
                                                            variant="base"
                                                        >
                                                            <T
                                                                {namespace}
                                                                translationKey="vip_tiers_points_multiplier_value"
                                                                translationOptions={{
                                                                    value: tier.pointsMultiplier,
                                                                }}
                                                            />
                                                        </Text>
                                                    {:else}
                                                        <RewardsVipTiersPageWidgetEmptyItem
                                                        />
                                                    {/if}
                                                </svelte:fragment>
                                                <svelte:fragment slot="reward">
                                                    {#if tier.rewarding}
                                                        <Text
                                                            component="span"
                                                            variant="base"
                                                            weight="normal"
                                                        >
                                                            <RewardsWidgetReward
                                                                reward={tier.rewarding}
                                                                {i18n}
                                                                {currencyFormatter}
                                                                {amountFormatter}
                                                                {namespace}
                                                                {pointsNamespace}
                                                                {urlsBuilder}
                                                                {sanitizer}
                                                            />
                                                        </Text>
                                                    {:else}
                                                        <RewardsVipTiersPageWidgetEmptyItem
                                                        />
                                                    {/if}
                                                </svelte:fragment>
                                                <svelte:fragment slot="perks">
                                                    {#if tier.perks}
                                                        <ul
                                                            class="gw-rd-vip-tiers-page-widget__slider__perks-list"
                                                        >
                                                            {#each tier.perks as perkItem}
                                                                <li
                                                                    class="gw-rd-vip-tiers-page-widget__slider__perks-list-item"
                                                                >
                                                                    <Text
                                                                        component="p"
                                                                        variant="base"
                                                                    >
                                                                        {perkItem}
                                                                    </Text>
                                                                </li>
                                                            {/each}
                                                        </ul>
                                                    {:else}
                                                        <RewardsVipTiersPageWidgetEmptyItem
                                                        />
                                                    {/if}
                                                </svelte:fragment>
                                            </RewardsVipTiersPageWidgetTierCard>
                                        {/each}
                                    </svelte:fragment>
                                </EmblaSlider>
                            </div>
                        </div>
                        <EmblaSliderDots
                            perPage={slidesPerPage}
                            {currentIndex}
                            slidesCount={$tierList$.length}
                            api={emblaApi || undefined}
                            class="gw-rd-vip-tiers-page-widget__dots"
                        >
                            <Icon width={10} height={10}>
                                <DotsIcon />
                            </Icon>
                        </EmblaSliderDots>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsVipTiersPageWidgetTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/globals.css";

    @define-mixin filled $firstSelector, $secondSelector {
        > div:$firstSelector
            .gw-rd-vip-tiers-page-widget-card__list-item:$secondSelector {
            background-color: var(--gw-rd-light-skyblue-bg-color);
        }
    }

    .gw-rd-vip-tiers-page-widget {
        background-color: var(--gw-white-bg);

        &__layout {
            width: 100%;
            display: flex;
            gap: clamp(
                var(--gw-rd-space-4),
                5.12px + 0.9vw,
                var(--gw-rd-space-8)
            );

            div.gw-rd-vip-tiers-page-widget__benefits {
                width: 100%;
                max-width: clamp(160px, 123.2px + 11.37vw, 260px);
                align-self: end;

                @mixin filled first-child, nth-child(even);
            }

            .gw-slider .gw-slider__slides > div {
                display: flex;
                gap: clamp(
                    var(--gw-rd-space-4),
                    5.12px + 0.9vw,
                    var(--gw-rd-space-8)
                );

                @mixin filled nth-child(odd), nth-child(odd);
                @mixin filled nth-child(even), nth-child(even);
            }
        }

        &__slider {
            width: 100%;
            max-width: clamp(170px, -131px + 80.25vw, 832px);

            &-arrows-wrapper {
                height: 44px;
            }

            @media (width <= 374px) {
                max-width: 140px;
            }
        }

        :global(.gw-dots.gw-rd-vip-tiers-page-widget__dots) {
            color: var(--gw-lightgrey-text-color);

            @media (width > 1000px) {
                display: none;
            }

            .gw-dots__button--active {
                color: var(--gw-blue-text-color);
            }
        }
    }
</style>
